import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import React, { useCallback, useMemo, useRef } from 'react';

import { captureException, captureMessage } from '@sentry/nextjs';
import config from 'config/server';
import { scrollToElement } from 'utils/scrollTo';

import FestiveBaubleDonationCampaignPage from 'components/FestiveBaubleDonationCampaignPage/FestiveBaubleDonationCampaignPage';
import GiftCardDonationCampaignPage from 'components/GiftCardDonationCampaignPage/GiftCardDonationCampaignPage';
import HeadWithSocialMedia from 'components/HeadWithSocialMedia/HeadWithSocialMedia';
import InnovationFundCampaignPage from 'components/InnovationFundDonationCampaignPage/InnovationFundDonationCampaignPage';
import Page404Content from 'components/Page404Content/Page404Content';
import ProductListDonationCampaignPage from 'components/ProductListDonationCampaignPage/ProductListDonationCampaignPage';
import RegularDonationCampaignPage from 'components/RegularDonationCampaignPage/RegularDonationCampaignPage';
import { useApp } from 'contexts/appContext';
import { IDonationCampaign } from 'models/donationCampaign';
import DonationCampaignTypes, {
  DonationCampaignType,
} from 'models/donationCampaignTypes';
import { ISalesOrderItem } from 'models/salesOrderItem';
import { IThankYouMessage } from 'models/thankYouMessage';
import { DonationCampaignPageService } from 'services/donationCampaignService';
import { DonationClientService } from 'services/donationClientService';
import { fetcher, fetcherStack9 } from 'services/httpRequestor';
import { ThankYouMessageService } from 'services/thankYouMessageService';

import MainLayout from '../../../layouts/MainLayout';

interface DonationCampaignProps {
  statusCode?: number;
  donationCampaign?: IDonationCampaign;
  messages: IThankYouMessage[];
  donations: ISalesOrderItem[];
  host?: string;
  slug?: string;
}

// This gets called on every request
export const getServerSideProps: GetServerSideProps = async ({
  query,
  req,
}) => {
  const campaignService = new DonationCampaignPageService(
    fetcher(req.headers.cookie),
  );
  const thankYouService = new ThankYouMessageService(
    fetcherStack9(config.stack9Token, config.stack9Url),
  );
  const donationService = new DonationClientService(
    fetcher(req.headers.cookie),
  );

  const { slug, ...getParams } = query;
  try {
    const { data: donationCampaign, status } =
      await campaignService.getOneByPageName(slug as string, {
        query: getParams,
      });

    if (status === 404) {
      return {
        props: {
          statusCode: 404,
        },
        notFound: true,
      };
    }
    let messages: IThankYouMessage[] = [];
    let donations: ISalesOrderItem[] = [];
    if (!donationCampaign) {
      captureMessage('System could not find donation campaign', {
        extra: { query: JSON.stringify(getParams), slug },
      });
    } else if (donationCampaign.show_messages) {
      [messages, donations] = await Promise.all([
        thankYouService.getMessages(),
        donationService.getAllDonations(),
      ]);
    }
    // Pass data to the page via props
    return {
      props: {
        slug,
        statusCode: status,
        donationCampaign,
        messages,
        donations,
      },
    };
  } catch (error) {
    captureException(error, { extra: { slug } });
    return {
      props: {
        slug,
        statusCode: error.status,
      },
    };
  }
};

const DonationCampaign: React.FC<DonationCampaignProps> = ({
  statusCode,
  donationCampaign,
  messages,
  donations,
  host,
  slug,
}) => {
  const donateNowRef = useRef<HTMLDivElement>(null);
  const { featuredDonationCampaignSlug } = useApp();
  const router = useRouter();

  const onClickCallToAction = useCallback(() => {
    const isFeaturedCampaign =
      router.query.slug === featuredDonationCampaignSlug;
    if (!isFeaturedCampaign) {
      router
        .push(
          {
            pathname: '/campaign/[slug]',
            query: {
              slug: featuredDonationCampaignSlug,
            },
          },
          `/campaign/${featuredDonationCampaignSlug}`,
        )
        .then(() => window.scrollTo(0, 0));
    }
    if (!donateNowRef?.current) {
      return;
    }
    scrollToElement(donateNowRef.current, 'top');
  }, [featuredDonationCampaignSlug, router]);

  const donationCampaignType = useMemo(() => {
    return Object.values(DonationCampaignTypes).includes(
      donationCampaign?.donation_campaign_type?.name as DonationCampaignType,
    )
      ? (donationCampaign?.donation_campaign_type?.name as DonationCampaignType)
      : DonationCampaignTypes.RegularDonation;
  }, [donationCampaign]);

  const renderCampaignByType = useCallback(() => {
    if (!donationCampaign) {
      return <h1>donation campaign is not set</h1>;
    }
    switch (donationCampaignType) {
      case DonationCampaignTypes.RegularDonation:
        return (
          <RegularDonationCampaignPage
            donationCampaign={donationCampaign}
            messages={messages}
            donations={donations}
            donateNowRef={donateNowRef}
          />
        );
      case DonationCampaignTypes.FestiveBauble:
        return (
          <FestiveBaubleDonationCampaignPage
            donationCampaign={donationCampaign}
          />
        );
      case DonationCampaignTypes.GiftCard:
        return (
          <GiftCardDonationCampaignPage donationCampaign={donationCampaign} />
        );
      case DonationCampaignTypes.ProductList:
        return (
          <ProductListDonationCampaignPage
            donationCampaign={donationCampaign}
            messages={messages}
            donations={donations}
            donateNowRef={donateNowRef}
          />
        );
      case DonationCampaignTypes.InnovationFund:
        return (
          <InnovationFundCampaignPage
            donationCampaign={donationCampaign}
            messages={messages}
            donations={donations}
            donateNowRef={donateNowRef}
          />
        );
      default:
        return <h1>{`${donationCampaignType} doesn't exist`}</h1>;
    }
  }, [donationCampaign, donationCampaignType, donations, messages]);

  if ((statusCode && statusCode >= 400) || !donationCampaign) {
    return <Page404Content />;
  }

  if (!donationCampaign) {
    captureMessage(
      "Forced return 404 page, stack9 or cache hasn't returned donation campaign",
      {
        extra: { slug },
      },
    );
    return <Page404Content />;
  }

  return (
    <>
      <HeadWithSocialMedia
        title={donationCampaign.title_meta}
        description={donationCampaign.description_meta}
        thumbnailURL={donationCampaign.banner_image?.url}
        url={`${host}/campaign/${router.query.slug}`}
        published_time={donationCampaign.published_datetime}
        keywords={donationCampaign.keywords}
        canonical_url={donationCampaign.canonical_url}
      />
      <MainLayout onClickCallToAction={onClickCallToAction}>
        {renderCampaignByType()}
      </MainLayout>
    </>
  );
};

export default DonationCampaign;
